import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, FormGroup, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ConnectedPosition } from '@angular/cdk/overlay';
import { MatSelectChange } from '@angular/material/select';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, combineLatest, filter, Observable } from 'rxjs';

import { FilterListItemChangeArgs, FilterListItemModel, FilterListItemValueModel } from 'tsui';
import { Map } from 'mapbox-gl';
import { Select, Store } from '@ngxs/store';
import { AccountService } from 'app/core';
import { RelatedToType } from '@shared/enums';
import { UserState } from '@state/user/state';
import { ResearchState } from '../state/state';
import UrlUtils from '@shared/Utils/url.utils';
import { FieldDef, QueryConfig } from '@core/models';
import { AccountState } from '@state/account/state';
import { Account } from '@core/models/account.types';
import { NewPermission } from '@core/enums/permission';
import { LookupService } from '@services/lookup.service';
import { Pipeline } from 'app/modules/projects/projects.types';
import { LayoutService } from '../../../layout/layout.service';
import { TsFilterUtilsService } from '@services/ts-filter-utils.service';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { BoundingBox, FilterForm, LatLng, MarkerType, ResearchLayoutKeys } from '../research.types';
import { OverlayHelperComponent } from '@shared/components/overlay-helper/overlay-helper.component';

@Component({
  selector: 'research-filters',
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ResearchFiltersComponent {
  filterParcelForm: UntypedFormGroup;
  account: Account;
  pipelines: Pipeline[];
  selectedView: Pipeline;
  NewPermission = NewPermission;
  filterForm = new FormGroup({
    owner: new FormControl<string | null>(null),
    owner_mailing_address: new FormControl<string | null>(null),
    zoning: new FormControl<string | null>(null),
    min_building_area_sq_ft: new FormControl<number | null>(null),
    max_building_area_sq_ft: new FormControl<number | null>(null),
    min_lot_area_acres: new FormControl<number | null>(null),
    max_lot_area_acres: new FormControl<number | null>(null),
    min_lot_area_sq_ft: new FormControl<number | null>(null),
    max_lot_area_sq_ft: new FormControl<number | null>(null),
    min_units: new FormControl<number | null>(null),
    max_units: new FormControl<number | null>(null),
    min_estimated_value: new FormControl<number | null>(null),
    max_estimated_value: new FormControl<number | null>(null),
  });
  position: ConnectedPosition = { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' };
  typeFields = new BehaviorSubject<FilterListItemModel[]>([]);
  filterValueList = new BehaviorSubject<FilterListItemValueModel[]>([]);

  private _queryFilters: Partial<QueryConfig.QueryFilters>;
  private fieldDefs: FieldDef[] = [];
  private hasUserInteraction = false;
  private destroyRef = inject(DestroyRef);

  @Input() mapStyle = '';
  @Input() mapInstance: Map;
  @Input() isQuerying = false;
  @Input() showGetLocation = false;
  @Input() set queryFilters(value: Partial<QueryConfig.QueryFilters>) {
    this._queryFilters = value;
    this.filterValueList.next(this.fjsonStringToFilterValue(this._queryFilters?.fjson ?? '[]'));
  }
  @Input() displayMarker: MarkerType | '' = '';
  @Output() editLayout = new EventEmitter<MarkerType>();
  @Output() getLocation = new EventEmitter();
  @Output() openResults = new EventEmitter();
  @Output() runFilter = new EventEmitter<FilterForm>();
  @Output() addressSelected = new EventEmitter<LatLng>();
  @Output() markersFilterChanged = new EventEmitter<any>();
  @Output() clearFilter = new EventEmitter();
  @Output() toggleMarkersToDisplay = new EventEmitter<MarkerType>();

  @ViewChild('filtersOverlay') private overlayHelper: OverlayHelperComponent;

  @Select(ResearchState.activeFilter) activeFilter$: Observable<[FilterForm, BoundingBox]>;
  @Select(ResearchState.currentFilterAppliedFieldsCount) currentFilterAppliedFieldsCount$: Observable<string>;
  @Select(ResearchState.currentBoundingBox) currentBoundingBox$: Observable<any>;

  readonly filterFormEmpty = new BehaviorSubject(true);
  readonly filterFormSameAsActiveFilter = new BehaviorSubject(false);

  constructor(
    private readonly _formBuilder: UntypedFormBuilder,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _accountService: AccountService,
    private readonly _layoutService: LayoutService,
    public readonly lookupService: LookupService,
    private readonly tsFilterUtilsService: TsFilterUtilsService,
    private readonly store: Store,
    private _projectsService: ProjectsService,
  ) {
    this.account = this.store.selectSnapshot(AccountState.getAccount);
    this._projectsService.accountId = this.account.id;
  }

  ngOnInit(): void {
    this.filterParcelForm = this._formBuilder.group({
      address: [''],
      latitude: [''],
      longitude: [''],
      property_use_standardized_code: [''],
      propertyUseCodeFilterCtrl: [''],
      showParcelBoundaries: [false],
      showFloodZones: [false],
    });

    this._projectsService.pipelines$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((pipelines) => {
      if (pipelines) {
        this.pipelines = pipelines?.sort((a, b) => a?.name.localeCompare(b?.name));
        this.fetchFieldDefs(this.displayMarker as MarkerType);
        this.setSavedSelectedView();
        this.updateSelectedFilter(this._queryFilters);
        this._changeDetectorRef.markForCheck();
      }
    });

    this._layoutService
      .getCommon(ResearchLayoutKeys.SEARCH_OWNER)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((search: { owner: string }) => search?.owner != null),
      )
      .subscribe((search: { owner: string }) => {
        this.filterForm.get<keyof FilterForm>('owner').setValue(search.owner);
        this.run();
      });

    this._layoutService
      .getCommon(ResearchLayoutKeys.SEARCH_MAILING_ADDRESS)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((search: { ownerMailingAddress: string }) => search?.ownerMailingAddress != null),
      )
      .subscribe((search: { ownerMailingAddress: string }) => {
        this.filterForm.get<keyof FilterForm>('owner_mailing_address').setValue(search.ownerMailingAddress);
        this.run();
      });

    combineLatest([this.filterForm.valueChanges, this.activeFilter$, this.currentBoundingBox$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([value, activeFilter, currentBoundingBox]) => {
        const isFilterFormEmpty = Object.values(value).every((v) => v === null);
        this.filterFormEmpty.next(isFilterFormEmpty);

        this.filterFormSameAsActiveFilter.next(
          activeFilter != null && this.isFilterFormSameAsActiveFilter(activeFilter, value, currentBoundingBox),
        );
      });
  }

  private setSavedSelectedView(): void {
    this.selectedView = this.pipelines.find(
      (pipeline) => pipeline.id === +UrlUtils.getFromLocalStorage('researchSelectedViewDealFilter', null),
    );
  }
  private fetchFieldDefs(relatedToType: MarkerType): void {
    this.fieldDefs = this.store.selectSnapshot(
      ObjectsManagerState.getFieldsByObjectName(relatedToType, {
        allowedFieldsOnly: true,
        addNestedFields: false,
      }),
    );

    this.typeFields.next(this.tsFilterUtilsService.getFilterList(this.fieldDefs));
  }

  public onAddFilterItem(): void {
    this.filterValueList.next([
      ...this.filterValueList.value,
      {
        fieldName: null,
        operator: null,
        value: null,
      },
    ]);
  }

  public onFilterDefChanged(filterDefChangeArgs: FilterListItemChangeArgs): void {
    const index = filterDefChangeArgs.index;
    delete filterDefChangeArgs.index;
    this.filterValueList.value[index] = filterDefChangeArgs;
    this.filterValueList.next([...this.filterValueList.value]);
  }

  public onFilterRemoved(index: number): void {
    this.hasUserInteraction = true;
    const newState = this.filterValueList.value?.filter((_, i) => i !== index);
    if (!newState.length) {
      this.onFilterCleared();
    }
    this.filterValueList.next(newState);
  }

  public onFilterCleared(): void {
    this.hasUserInteraction = true;
    this.filterValueList.next([]);
    this._queryFilters = { fjson: `[]` };
    if (this.displayMarker === RelatedToType.Project) {
      this.selectedView = null;
      UrlUtils.saveToLocalStorage('researchSavedViewDealFilter', null);
    }
    this.updateSelectedFilter(this._queryFilters);
  }

  public onFilterApplied(): void {
    this.hasUserInteraction = true;
    this._queryFilters = {
      fjson: `[${this.filterValueList.value
        .map((x) => `["${x.fieldName}", "${x.operator}", ${JSON.stringify(x.value)}]`)
        .join(', ')}]`,
    };

    this.updateSelectedFilter(this._queryFilters);
  }

  setAddressParts(addressParts): void {
    this.addressSelected.emit({ latitude: addressParts.lat, longitude: addressParts.lng });
  }

  getUserLocation(): void {
    this.getLocation.emit();
  }

  onEditLayout(): void {
    this.editLayout.emit(this.displayMarker as MarkerType);
  }

  run(): void {
    this.runFilter.emit(this.filterForm.getRawValue());

    this.filterFormSameAsActiveFilter.next(true);
    this.overlayHelper.close();
  }

  clear(): void {
    this.filterForm.reset();
    this.filterForm.markAsDirty();
    this.clearFilter.emit();
    this.overlayHelper.close();
  }

  onChangeDisplayMarker(event: { value: MarkerType }): void {
    this.hasUserInteraction = true;
    this.toggleMarkersToDisplay.emit(event.value);
    this.fetchFieldDefs(event.value);
  }

  onChangeSelectedView(event: MatSelectChange): void {
    this.hasUserInteraction = true;
    const user = this.store.selectSnapshot(UserState.getUser);
    this.selectedView = event.value as Pipeline;

    if (this.selectedView) {
      // Dynamic view
      if (this.selectedView.saved_filter_id) {
        this._accountService
          .getSavedFilterById(this.selectedView.saved_filter_id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((savedFilter) => {
            this._queryFilters = JSON.parse(savedFilter.filters);
          });
      }
      // Static view
      else {
        this._queryFilters = { fjson: '[["pipeline_id","in",[' + this.selectedView.id.toString() + ']]]' };
      }
    }
    // No view selected
    else {
      this._queryFilters = { fjson: user?.settings?.map_markers_selected?.deals ?? '[]' };
    }
    this.updateSelectedFilter(this._queryFilters);
    this._changeDetectorRef.markForCheck();
  }

  private fjsonStringToFilterValue(fjson: string): FilterListItemValueModel[] {
    try {
      const parsed = JSON.parse(fjson ?? '[]');

      // If parsed is not an array, return empty array
      if (!Array.isArray(parsed)) {
        console.warn(
          '[ResearchFiltersComponent] [fjsonStringToFilterValue] Invalid fjson format: expected array but got ',
          typeof parsed,
        );
        return [];
      }

      // Filter out invalid entries and map valid ones
      return parsed
        .filter(
          (item): item is [string, FilterListItemValueModel['operator'], any] =>
            Array.isArray(item) &&
            item.length === 3 &&
            typeof item[0] === 'string' &&
            typeof item[1] === 'string' &&
            ['eq', 'gt', 'gte', 'lt', 'lte', 'contains', 'like', 'in', 'not', 'is'].includes(item[1]) &&
            item[2] !== undefined,
        )
        .map(([fieldName, operator, value]) => ({
          fieldName,
          operator,
          value,
        }));
    } catch (error) {
      console.warn('[ResearchFiltersComponent] [fjsonStringToFilterValue] Error parsing fjson: \n', fjson, error);
      return [];
    }
  }

  private updateSelectedFilter(markersFilter: Partial<QueryConfig.QueryFilters>): void {
    if (markersFilter) {
      this._queryFilters = markersFilter;
      this.filterValueList.next(this.fjsonStringToFilterValue(this._queryFilters?.fjson ?? '[]'));
      if (this.hasUserInteraction) {
        this.markersFilterChanged.emit(this._queryFilters);
        UrlUtils.saveToLocalStorage('researchSelectedViewDealFilter', +this.selectedView?.id);
      }
    }
  }

  private isFilterFormSameAsActiveFilter(
    activeFilter: [FilterForm, BoundingBox],
    form: Partial<FilterForm>,
    currentBoundingBox: BoundingBox,
  ): boolean {
    const [activeFilterForm, activeBoundingBox] = activeFilter;
    return (
      Object.keys(form).every((key) => activeFilterForm[key] === form[key]) &&
      this.equalBoundingBoxes(activeBoundingBox, currentBoundingBox)
    );
  }

  private equalBoundingBoxes(activeBoundingBox: BoundingBox, currentBoundingBox: BoundingBox) {
    return (
      activeBoundingBox?.sw[0] === currentBoundingBox?.sw[0] &&
      activeBoundingBox?.sw[1] === currentBoundingBox?.sw[1] &&
      activeBoundingBox?.ne[0] === currentBoundingBox?.ne[0] &&
      activeBoundingBox?.ne[1] === currentBoundingBox?.ne[1]
    );
  }
}
